import { ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { ColumnDef } from '@tanstack/react-table'
import {
	ArrowDown,
	ArrowUp,
	ChevronsUpDown,
	Eye,
	Loader2,
	MessageCircleMoreIcon,
	MessageCirclePlusIcon,
} from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'

import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'

import { TaskSquareIcon } from '@/assets/icons'
import {
	SalesRecordDialogState,
	SalesRecordsDialog,
	SalesRecordsHistoryDialog,
} from '@/components/sales-records-dialog'
import { Button } from '@/components/ui/button'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { PaginationComponent } from '@/components/ui/pagination'
import {
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { formatIban } from '@/lib/helper'
import { cn } from '@/lib/utils'
import bookingService from '@/services/booking-service'
import userService from '@/services/user-service'
import { format, isAfter } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

export const ReferralsTable = () => {
	const searchableColumns = [
		{ label: 'Besitzer-ID', value: 'owner_id' },
		{ label: 'Benutzt von User ID', value: 'used_by_user_id' },
	]

	const isSmallScreen = useMediaQuery('(max-width: 64rem)')

	const [expandedRows, setExpandedRows] = useState<number[]>([])
	const [expandedSubRows, setExpandedSubRows] = useState<number[]>([])
	const [filterValue, setFilterValue] = useState('')
	const [searchValue, setSearchValue] = useState('')
	const [page, setPage] = useState(1)
	const [selectedColumn, setSelectedColumn] = useState('')
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [sortColumn, setSortColumn] = useState('')
	const [sortDirection, setSortDirection] = useState('')
	const [isUserDetailDialogVisible, setIsUserDetailDialogVisible] = useState<User | null>(null)
	const [isNoteDialogVisible, setIsNoteDialogVisible] = useState<{
		id: number | null
		note: string
		type: string | null
	}>({ id: null, note: '', type: null })
	const [isSalesRecordDialogVisible, setIsSalesRecordDialogVisible] =
		useState<SalesRecordDialogState>(null)
	const [isSalesRecordHistoryDialogVisible, setIsSalesRecordHistoryDialogVisible] =
		useState<SalesRecordDialogState>(null)

	const { data, refetch, isLoading } = useQuery(
		'fetch-referral-users',
		() =>
			userService.fetchReferralUsersData({
				pageNumber: page.toString(),
				pageSize: rowsPerPage.toString(),
				filterColumn: selectedColumn,
				filterValue,
			}),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching referrals')
			},
		},
	)

	useEffect(() => {
		refetch()
	}, [rowsPerPage, page])

	const { mutate: updateNote, isLoading: isUpdatingNote } = useMutation({
		mutationKey: 'update-note-referral',
		mutationFn: ({
			note,
			id,
		}: {
			note: { user_notes: string } | { booking_notes: string }
			id: number
		}) => bookingService.updateNote(note, id),
		onSuccess: () => {
			toast.success('Hinweis erfolgreich aktualisiert')
			setIsNoteDialogVisible({ note: '', id: null, type: null })
			refetch()
		},
		onError: (err: any) => {
			toast.error(
				err?.response?.data?.message ??
					'Hinweis konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
			)
		},
	})

	const handleNoteSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		const formData = new FormData(event.currentTarget)
		const formJson = Object.fromEntries((formData as any).entries())
		const note = String(formJson.note ?? '')
		if (isNoteDialogVisible.id) {
			if (isNoteDialogVisible.type === 'booking') {
				updateNote({ note: { booking_notes: note }, id: isNoteDialogVisible.id })
			} else {
				updateNote({ note: { user_notes: note }, id: isNoteDialogVisible.id })
			}
		}
	}

	const handleSalesRecordDialogOpen = (
		referral: Referral['referrals'][0],
		ownerUser: User | null,
	) => {
		if (!referral.booking_id || !referral.used_by_user_id) return
		setIsSalesRecordDialogVisible({
			bookingId: referral.booking_id,
			userId: referral.used_by_user_id,
			userName: ownerUser
				? `${ownerUser.first_name} ${ownerUser.last_name}`.trim()
				: String(referral.used_by_user_id),
		})
	}

	const handleSalesRecordHistoryDialogOpen = (
		referral: Referral['referrals'][0],
		ownerUser: User | null,
	) => {
		if (!referral.booking_id || !referral.used_by_user_id) return
		setIsSalesRecordHistoryDialogVisible({
			bookingId: referral.booking_id,
			userId: referral.used_by_user_id,
			userName: ownerUser
				? `${ownerUser.first_name} ${ownerUser.last_name}`.trim()
				: String(referral.used_by_user_id),
		})
	}

	const sortedData = useMemo(() => {
		if (!data) return []
		if (!sortColumn) return data.data

		return [...data.data].sort((a, b) => {
			const aValue = a[sortColumn as keyof Referral]
			const bValue = b[sortColumn as keyof Referral]

			if (aValue == null || bValue == null) return 0

			let aVal: any = aValue
			let bVal: any = bValue

			if (aVal instanceof Date) aVal = aVal.getTime()
			if (bVal instanceof Date) bVal = bVal.getTime()

			const aParsed = typeof aVal === 'string' ? Date.parse(aVal) : NaN
			const bParsed = typeof bVal === 'string' ? Date.parse(bVal) : NaN

			if (!isNaN(aParsed) && !isNaN(bParsed)) {
				aVal = aParsed
				bVal = bParsed
			}

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
			}

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
			}

			return 0
		})
	}, [data, sortColumn, sortDirection])

	const filteredReferrals = useMemo(() => {
		return sortedData?.filter(curr => {
			if (!searchValue) return true
			return curr?.owner_id.toString().includes(searchValue.toLowerCase())
		})
	}, [sortedData, searchValue])

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortColumn(column)
			setSortDirection('asc')
		}
	}

	const toggleRow = (id: number) => {
		setExpandedRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const toggleSubRow = (id: number) => {
		setExpandedSubRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const columns: ColumnDef<Referral>[] = [
		{
			accessorKey: 'owner_id',
			header: ({ column }) => (
				<DataTableColumnHeader className="max-w-[200px]" column={column} title="Besitzer-ID" />
			),
			cell: ({ row }) => (
				<div
					onClick={() => row.toggleExpanded()}
					className="flex cursor-pointer ml-14 items-center gap-1">
					<ChevronRightIcon
						className={cn('text-secondary size-4', { 'rotate-90': row.getIsExpanded() })}
					/>
					{row.getValue('owner_id')}
				</div>
			),
		},
		{
			accessorKey: 'totalReferrals',
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Gesamtzahl der Empfehlungen" />
			),
		},
		{
			accessorKey: 'used_at',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Verwendet am" />,
			cell: () => <div>{format(new Date(), 'yyyy-MM-dd HH:mm:ss')}</div>,
		},
	]

	const subReferralsTable = (rowData: Referral) => {
		return (
			<Table className="border-separate border-spacing-y-3">
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] rounded-xl h-14">
						<TableHead className="px-4 text-center rounded-l-xl" align="center">
							ID
						</TableHead>
						<TableHead className="px-4 text-center" align="center">
							Empfehlungscode
						</TableHead>
						<TableHead className="px-4 text-center" align="center">
							Benutzt von User ID
						</TableHead>
						<TableHead className="px-4 text-center" align="center">
							Buchungs-ID
						</TableHead>
						<TableHead className="px-4 text-center rounded-r-xl" align="center">
							Ist eingelöst
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rowData.referrals.map((referral, index) => (
						<TableRow key={referral.id} className="bg-white border-0 h-14">
							<TableCell
								align="center"
								className={cn('text-secondary text-sm font-medium', {
									'border-t border-[#EEEFF0]': index > 0,
								})}>
								{referral.id}
							</TableCell>
							<TableCell
								align="center"
								className={cn('text-secondary text-sm font-medium', {
									'border-t border-[#EEEFF0]': index > 0,
								})}>
								{referral.referral_code}
							</TableCell>
							<TableCell
								align="center"
								className={cn('text-secondary text-sm font-medium', {
									'border-t border-[#EEEFF0]': index > 0,
								})}>
								<div className="flex items-center justify-center gap-1">
									<div
										onClick={() => handleSalesRecordDialogOpen(referral, referral.used_by_user)}
										className="cursor-pointer rounded-[18px] w-fit pr-1">
										<MessageCirclePlusIcon className="size-4" />
									</div>
									<div
										onClick={() =>
											handleSalesRecordHistoryDialogOpen(referral, referral.used_by_user)
										}
										className="cursor-pointer rounded-[18px] w-fit pr-1">
										<Eye className="size-4" />
									</div>
									<div
										onClick={() => {
											if (referral.used_by_user) setIsUserDetailDialogVisible(referral.used_by_user)
										}}
										className="bg-green-200/80 cursor-pointer p-1.5 rounded-[18px] w-fit px-3">
										{referral.used_by_user_id}
									</div>
									<div
										onClick={() =>
											setIsNoteDialogVisible({
												note: referral.user_notes ?? '',
												id: referral.booking_id,
												type: 'user',
											})
										}
										className="cursor-pointer relative rounded-[18px] w-fit pl-1">
										<MessageCircleMoreIcon className="size-4" />
										<div
											className={cn('size-2 rounded-full bg-red-500 absolute top-0 -right-1', {
												hidden: referral.user_notes === null || referral.user_notes === '',
											})}
										/>
									</div>
								</div>
							</TableCell>
							<TableCell
								align="center"
								className={cn('text-secondary text-sm font-medium', {
									'border-t border-[#EEEFF0]': index > 0,
								})}>
								<div className="flex items-center justify-center gap-1">
									<div
										className={cn('bg-yellow-200/80 p-1.5 rounded-[18px] w-fit px-3', {
											'bg-purple-200/80': isAfter(
												new Date(referral.createdAt).setHours(0, 0, 0, 0),
												new Date('2026-03-08').setHours(0, 0, 0, 0),
											),
										})}>
										{referral.booking_id}
									</div>
									<div
										onClick={() =>
											setIsNoteDialogVisible({
												note: referral.booking_notes ?? '',
												id: referral.booking_id,
												type: 'booking',
											})
										}
										className="cursor-pointer relative rounded-[18px] w-fit pl-1">
										<MessageCircleMoreIcon className="size-4" />
										<div
											className={cn('size-2 rounded-full bg-red-500 absolute top-0 -right-1', {
												hidden: referral.booking_notes === null || referral.booking_notes === '',
											})}
										/>
									</div>
								</div>
							</TableCell>
							<TableCell
								align="center"
								className={cn('text-secondary text-sm font-medium', {
									'border-t border-[#EEEFF0]': index > 0,
								})}>
								<div
									className={cn(
										'w-fit rounded-[18px] p-1.5 px-3 mx-auto',
										referral.is_redeemed === 1
											? 'bg-[#61B9641A] text-[#61B964]'
											: 'bg-[#FFEFF2] text-[#DB5358]',
									)}>
									{referral.is_redeemed === 1 ? 'wahr' : 'falsch'}
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		)
	}

	const MobileReferralsTable = () => {
		return (
			<Table>
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							onClick={() => handleSort('owner_id')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Besitzer-ID
								{sortColumn === 'owner_id' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('totalReferrals')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Gesamtzahl der Empfehlungen
								{sortColumn === 'totalReferrals' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('used_at')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Verwendet am
								{sortColumn === 'used_at' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{data?.data.map((referral, index) => (
						<React.Fragment key={referral.owner_id.toString() + index.toString()}>
							<TableRow
								className={cn(
									'h-14',
									expandedRows.includes(referral.owner_id)
										? '!border-b-0'
										: 'border-b border-[#F4F4F4]',
								)}
								onClick={() => toggleRow(referral.owner_id)}>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(referral.owner_id),
									})}>
									<div className="flex items-center">
										<ChevronRightIcon
											className={cn(
												'size-3 shrink-0 -mt-0.5',
												expandedRows.includes(referral.owner_id)
													? 'rotate-90 text-secondary'
													: 'text-muted-foreground',
											)}
										/>
										{referral.owner_id}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-[10px]', {
										'text-muted-foreground': !expandedRows.includes(referral.owner_id),
									})}>
									{referral.totalReferrals}
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-[10px]', {
										'text-muted-foreground': !expandedRows.includes(referral.owner_id),
									})}>
									<div>{format(new Date(), 'yyyy-MM-dd HH:mm:ss')}</div>
								</TableCell>
							</TableRow>
							{expandedRows.includes(referral.owner_id) && (
								<TableRow
									className={cn(
										'mt-2',
										!expandedRows.includes(referral.owner_id)
											? '!border-b-0'
											: 'border-b border-[#F4F4F4]',
									)}>
									<TableCell className="pt-0" colSpan={3}>
										<Table>
											<TableHeader>
												<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
													<TableHead
														align="center"
														className="text-center text-muted-foreground text-[10px] font-medium">
														ID
													</TableHead>
													<TableHead
														align="center"
														className="text-center text-muted-foreground text-[10px] font-medium">
														Empfehlungscode
													</TableHead>
													<TableHead
														align="center"
														className="text-center text-muted-foreground text-[10px] font-medium">
														Benutzt von User ID
													</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{referral.referrals.map((r, subIndex) => (
													<React.Fragment key={r.id.toString() + subIndex.toString()}>
														<TableRow
															className={cn(
																'h-14',
																expandedSubRows.includes(r.id)
																	? '!border-b-0'
																	: 'border-b border-[#F4F4F4]',
															)}
															onClick={() => toggleSubRow(r.id)}>
															<TableCell
																align="center"
																className={cn('text-xs', {
																	'text-muted-foreground': !expandedSubRows.includes(r.id),
																})}>
																<div className="flex items-center">
																	<ChevronRightIcon
																		className={cn(
																			'size-3 shrink-0 -mt-0.5',
																			expandedSubRows.includes(r.id)
																				? 'rotate-90 text-secondary'
																				: 'text-muted-foreground',
																		)}
																	/>
																	{r.id}
																</div>
															</TableCell>
															<TableCell
																align="center"
																className={cn('text-[10px]', {
																	'text-muted-foreground': !expandedSubRows.includes(r.id),
																})}>
																<div>{r.referral_code}</div>
															</TableCell>
															<TableCell
																align="center"
																className={cn('text-[10px]', {
																	'text-muted-foreground': !expandedSubRows.includes(r.id),
																})}>
																<div className="flex items-center justify-center gap-1">
																	<div
																		onClick={e => {
																			e.stopPropagation()
																			handleSalesRecordDialogOpen(r, r.used_by_user)
																		}}
																		className="cursor-pointer rounded-[18px] w-fit pr-1">
																		<MessageCirclePlusIcon className="size-3" />
																	</div>
																	<div
																		onClick={e => {
																			e.stopPropagation()
																			handleSalesRecordHistoryDialogOpen(r, r.used_by_user)
																		}}
																		className="cursor-pointer rounded-[18px] w-fit pr-1">
																		<Eye className="size-3" />
																	</div>
																	<div
																		onClick={e => {
																			e.stopPropagation()
																			if (r.used_by_user)
																				setIsUserDetailDialogVisible(r.used_by_user)
																		}}
																		className="bg-green-200/80 cursor-pointer p-1 px-2 rounded-[18px] w-fit text-[10px]">
																		{r.used_by_user_id}
																	</div>
																	<div
																		onClick={e => {
																			e.stopPropagation()
																			setIsNoteDialogVisible({
																				note: r.user_notes ?? '',
																				id: r.booking_id,
																				type: 'user',
																			})
																		}}
																		className="cursor-pointer relative rounded-[18px] w-fit pl-1">
																		<MessageCircleMoreIcon className="size-3" />
																		<div
																			className={cn(
																				'size-2 rounded-full bg-red-500 absolute top-0 -right-1',
																				{
																					hidden: r.user_notes === null || r.user_notes === '',
																				},
																			)}
																		/>
																	</div>
																</div>
															</TableCell>
														</TableRow>
														{expandedSubRows.includes(r.id) && (
															<TableRow
																className={cn(
																	'mt-2',
																	!expandedSubRows.includes(r.id)
																		? '!border-b-0'
																		: 'border-b border-[#F4F4F4]',
																)}>
																<TableCell className="pt-0" colSpan={3}>
																	<div className="px-3 flex flex-col gap-4">
																		<div className="flex items-center justify-between">
																			<h6 className="text-muted-foreground text-[10px] font-medium">
																				Buchungs-ID
																			</h6>
																			<div className="flex items-center gap-1">
																				<div
																					className={cn(
																						'bg-yellow-200/80 p-1 px-2 rounded-[18px] w-fit text-[10px]',
																						{
																							'bg-purple-200/80': isAfter(
																								new Date(r.createdAt).setHours(0, 0, 0, 0),
																								new Date('2026-03-08').setHours(0, 0, 0, 0),
																							),
																						},
																					)}>
																					{r.booking_id}
																				</div>
																				<div
																					onClick={e => {
																						e.stopPropagation()
																						setIsNoteDialogVisible({
																							note: r.booking_notes ?? '',
																							id: r.booking_id,
																							type: 'booking',
																						})
																					}}
																					className="cursor-pointer relative rounded-[18px] w-fit pl-1">
																					<MessageCircleMoreIcon className="size-3" />
																					<div
																						className={cn(
																							'size-2 rounded-full bg-red-500 absolute top-0 -right-1',
																							{
																								hidden:
																									r.booking_notes === null ||
																									r.booking_notes === '',
																							},
																						)}
																					/>
																				</div>
																			</div>
																		</div>
																		<div className="flex items-center justify-between">
																			<h6 className="text-muted-foreground text-[10px] font-medium">
																				Ist eingelöst
																			</h6>
																			<div
																				className={cn(
																					'text-[10px] w-fit p-1.5 px-2 rounded-[18px]',
																					r.is_redeemed === 1
																						? 'bg-[#61B9641A] text-[#61B964]'
																						: 'bg-[#FFEFF2] text-[#DB5358]',
																				)}>
																				{r.is_redeemed === 1 ? 'wahr' : 'falsch'}
																			</div>
																		</div>
																	</div>
																</TableCell>
															</TableRow>
														)}
													</React.Fragment>
												))}
											</TableBody>
										</Table>
									</TableCell>
								</TableRow>
							)}
						</React.Fragment>
					))}
				</TableBody>
			</Table>
		)
	}

	return (
		<div className="rounded-[20px] bg-white max-lg:p-4 max-lg:pr-3 lg:px-4 lg:py-6 flex flex-col">
			<SalesRecordsDialog
				dialogState={isSalesRecordDialogVisible}
				onClose={() => setIsSalesRecordDialogVisible(null)}
				onSuccess={() => refetch()}
			/>
			<SalesRecordsHistoryDialog
				dialogState={isSalesRecordHistoryDialogVisible}
				onClose={() => setIsSalesRecordHistoryDialogVisible(null)}
				onSuccess={() => refetch()}
			/>

			<ResponsiveModal
				open={isNoteDialogVisible.id !== null}
				onOpenChange={() => setIsNoteDialogVisible({ note: '', id: null, type: null })}>
				<ResponsiveModalContent
					crossIconClassName="top-3 lg:top-4.5"
					className="!px-6 !py-4 !max-w-[550px] gap-0">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							{isNoteDialogVisible.type === 'booking' ? 'Buchungshinweis' : 'Benutzerhinweis'}
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							{isNoteDialogVisible.type === 'booking' ? 'Buchungshinweis' : 'Benutzerhinweis'}
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<form onSubmit={handleNoteSubmit} className="mt-3 lg:mt-4 flex flex-col">
						<Textarea
							defaultValue={isNoteDialogVisible.note}
							className="resize-none max-lg:placeholder:text-sm min-h-[117px]"
							name="note"
							placeholder="Notiz hinzufügen"
						/>
						<Button
							variant="secondary"
							className="mt-6 w-full lg:h-[44px] rounded-[12px] font-extrabold text-white">
							{isUpdatingNote && <Loader2 className="animate-spin" />}
							{isUpdatingNote ? 'Bitte warten' : 'Speichern'}
						</Button>
					</form>
				</ResponsiveModalContent>
			</ResponsiveModal>

			<ResponsiveModal
				open={!!isUserDetailDialogVisible}
				onOpenChange={() => setIsUserDetailDialogVisible(null)}>
				<ResponsiveModalContent className="gap-0 !max-w-[550px]">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Benutzerdetails
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Benutzerdetails
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<div className="mt-8 lg:mt-12 flex flex-col gap-4 lg:gap-5">
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">ID</p>
							<p className="text-secondary font-extrabold">{isUserDetailDialogVisible?.id}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Vorname</p>
							<p className="text-secondary capitalize font-extrabold">
								{isUserDetailDialogVisible?.first_name}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Nachname</p>
							<p className="text-secondary font-extrabold capitalize">
								{isUserDetailDialogVisible?.last_name}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">E-Mail</p>
							<p className="text-secondary font-extrabold">{isUserDetailDialogVisible?.email}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Telefon</p>
							<p className="text-secondary font-extrabold">
								{isUserDetailDialogVisible?.phone_number}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Land</p>
							<p className="capitalize text-secondary font-extrabold">
								{isUserDetailDialogVisible?.country}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">PLZ</p>
							<p className="text-secondary font-extrabold">{isUserDetailDialogVisible?.zip}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Stadt</p>
							<p className="capitalize text-secondary font-extrabold">
								{isUserDetailDialogVisible?.country}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Adresse</p>
							<p className="capitalize text-secondary font-extrabold">
								{isUserDetailDialogVisible?.address}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">IBAN</p>
							<p className="text-secondary font-extrabold">
								{formatIban(isUserDetailDialogVisible?.iban)}
							</p>
						</div>
						<div className="flex items-center justify-between">
							<p className="text-secondary/50 font-medium">Erstellt am</p>
							{isUserDetailDialogVisible && (
								<p className="text-secondary font-extrabold">
									{format(
										toZonedTime(new Date(isUserDetailDialogVisible.createdAt), 'UTC'),
										'dd.MM.yyyy, HH:mm',
									)}
								</p>
							)}
						</div>
					</div>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<div className="flex items-center gap-2">
				<div className="lg:hidden rounded-xl border border-[#EEEFF0] size-10 flex items-center justify-center">
					<TaskSquareIcon className="text-secondary" />
				</div>
				<h1 className="text-muted-foreground lg:text-secondary text-xs lg:text-2xl font-medium lg:font-extrabold">
					Listen von Empfehlungsbenutzern
				</h1>
			</div>

			<div className="flex max-lg:mt-5 mt-6 flex-col gap-3 lg:gap-4">
				<div className="flex lg:flex-row lg:gap-4 flex-col gap-3 lg:items-center max-lg:justify-between">
					<div className="grid grid-cols-2 items-center gap-[9px] lg:hidden">
						<div className="relative flex items-center">
							<Input
								value={searchValue}
								onChange={e => setSearchValue(e.target.value)}
								className="!pl-[34px] h-10 text-xs"
								placeholder="Suche..."
							/>
							<MagnifyingGlassIcon className="size-4 text-muted-foreground absolute left-2" />
						</div>
						<Select
							value={rowsPerPage.toString()}
							onValueChange={value => setRowsPerPage(Number(value))}>
							<SelectTrigger className="w-full h-10 text-xs">
								<SelectValue placeholder="zeigen 50" />
							</SelectTrigger>
							<SelectContent>
								{[50, 80, 100, 200, 500].map(pageSize => (
									<SelectItem key={pageSize} value={pageSize.toString()}>
										zeigen {pageSize}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
				<div className="lg:hidden flex flex-col gap-3">
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="h-10 text-xs w-full">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<div className="flex lg:hidden items-center gap-2">
						<Input
							value={filterValue}
							onChange={e => setFilterValue(e.target.value)}
							className="h-10 text-xs"
							placeholder="Bezeichnung eingeben"
						/>
						<Button
							onClick={() => refetch()}
							variant="secondary"
							className="w-[73px] py-3 text-xs font-medium bg-secondary rounded-lg">
							Suche
						</Button>
					</div>
				</div>
				<div className="flex max-lg:hidden items-center gap-3">
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="flex-1">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Input
						value={filterValue}
						onChange={e => setFilterValue(e.target.value)}
						className="flex-1"
						placeholder="Bezeichnung eingeben"
					/>
					<Button
						onClick={() => refetch()}
						variant="secondary"
						className="w-[90px] font-medium bg-secondary rounded-xl">
						Suche
					</Button>
				</div>
			</div>
			<div className="flex max-lg:hidden items-center justify-between mt-6">
				<div className="flex items-center gap-4">
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-[127px]">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-8">
					<PaginationComponent
						currentPage={page}
						totalPages={data?.pagination?.totalPages ?? 1}
						onPageChange={setPage}
					/>
					<div className="relative flex items-center">
						<Input
							value={searchValue}
							onChange={e => setSearchValue(e.target.value)}
							className="!pl-[38px]"
							placeholder="Suche..."
						/>
						<MagnifyingGlassIcon className="size-5 text-muted-foreground absolute left-2" />
					</div>
				</div>
			</div>
			<div className="mt-4">
				{isSmallScreen ? (
					<MobileReferralsTable />
				) : (
					<DataTable
						columns={columns}
						data={filteredReferrals ?? []}
						expandableComponent={subReferralsTable}
						isExpandable
						subRow="referrals"
						isLoading={isLoading}
					/>
				)}
			</div>
			<div className="mt-6 lg:mt-16 flex justify-center">
				<PaginationComponent
					currentPage={page}
					totalPages={data?.pagination.totalPages ?? 1}
					onPageChange={setPage}
				/>
			</div>
		</div>
	)
}
